import { ReactComponentElement } from "react";
import { Resolver } from "react-hook-form";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";

import { Nullable } from "../types/common";
import { BriefType } from "../constants/types";
import {
  IExpectedKey,
  IOptionLists,
} from "../components/filters/components/filter-chips/filter-chips.props";
import { IParam } from "./useUrlQueryString";

import { toDatePickerValue } from "../utils/date-format.utils";
import { getUsers } from "../features/users/store/users.store";
import { selectRegionResults } from "../redux/selectors/commonSelectors";

import {
  CampaignBriefFilter,
  ExecutionBriefFilter,
  FlyerFilters,
  WorkOrderFilter,
} from "../components/filters/forms";
import { mergedApprovalStatuses } from "../types/approval-status/approval-statuses.constant";
import { CampaignBriefTemplateLabels } from "../types/templates/campaign-brief/campaign-brief-template-labels.constant";
import { ExecutionBriefTemplateLabels } from "../types/templates/execution-brief/execution-brief-template-labels.constant";
import { BasicResourceType } from "../types/resources/resources.enums";
import { notFlag } from "../components/filters/forms/filters.constants";

export interface IFormConfiguration {
  Fields: (params: any) => ReactComponentElement<any>;
  paramsToValues: (params: IParam[], options: IOptionLists) => Record<string, any>;
  validation: Nullable<Resolver<Record<string, any>>>;
  valuesToParams: (params: Record<string, any>) => IParam[];
}

const useStyles = makeStyles(() => ({
  filterBriefOwner: {
    background: "#5B5EA6",
    color: "#fff",
    "&:focus": {
      background: "#5B5EA6",
    },
  },
  filterTemplate: {
    background: "#45B8AC",
    color: "#fff",
    "&:focus": {
      background: "#45B8AC",
    },
  },
  filterDateRange: {
    background: "#98B4D4",
    color: "#fff",
    "&:focus": {
      background: "#98B4D4",
    },
  },
  filterRegion: {
    background: "#E15D44",
    color: "#fff",
    "&:focus": {
      background: "#E15D44",
    },
  },
  filterStatus: {
    background: "#955251",
    color: "#fff",
    "&:focus": {
      background: "#955251",
    },
  },
}));

interface TUseFilterConfig {
  chipConfig?: IExpectedKey[];
  Form: Nullable<IFormConfiguration>;
}

function useFilterConfig(filterType: string): TUseFilterConfig {
  const styles = useStyles();
  const allUsers = useSelector(getUsers);
  const allRegions = useSelector(selectRegionResults);

  // Formatting functions
  const campaignTemplate = (templateId: string): Nullable<string> => {
    const template = CampaignBriefTemplateLabels.find((t: any) => t.value === templateId);
    return template?.label ?? null;
  };

  const executionTemplate = (templateId: string): Nullable<string> => {
    const template = ExecutionBriefTemplateLabels.find((t: any) => t.value === templateId);
    return template?.label ?? null;
  };

  const dateFrom = (dateFrom: string, fields: any): string => {
    const label = !!fields.dateTo ? "From" : "After";
    const date = toDatePickerValue(dateFrom);

    return `${label} : ${date}`;
  };

  const dateTo = (dateTo: string, fields: any): string => {
    const label = !!fields.dateFrom ? "Until" : "Before";
    const date = toDatePickerValue(dateTo);

    return `${label} : ${date}`;
  };

  const region = (regionId: string): Nullable<string> => {
    const region = allRegions.find((r: any) => r.id === Number(regionId));
    return region?.name ?? null;
  };

  const status = (statusId: string): Nullable<string> => {
    const not = statusId.substr(0, notFlag.length) === notFlag;
    const status = mergedApprovalStatuses.find(
      (s: any) => s.value === statusId.replace(notFlag, "")
    );

    if (status) {
      return (not ? "Not: " : "") + status.label;
    }

    return null;
  };

  const userName = (userId: string): Nullable<string> => {
    const user = allUsers?.find((u: any) => u.id === Number(userId));
    return user?.fullName ?? null;
  };

  // Chip configuration
  const commonChips = [
    { name: "dateFrom", class: styles.filterDateRange, render: dateFrom },
    { name: "dateTo", class: styles.filterDateRange, render: dateTo },
    { name: "regions", class: styles.filterRegion, render: region },
    { name: "status", class: styles.filterStatus, render: status },
  ];

  let chipConfig;
  let Form: Nullable<IFormConfiguration> = null;

  switch (filterType) {
    case BasicResourceType.Flyer:
      chipConfig = [
        ...commonChips,
        { name: "flyerOwner", class: styles.filterBriefOwner, render: userName },
      ];
      Form = FlyerFilters;
      break;

    case BriefType.CAMPAIGN_BRIEF:
      chipConfig = [
        ...commonChips,
        { name: "briefOwner", class: styles.filterBriefOwner, render: userName },
        { name: "template", class: styles.filterTemplate, render: campaignTemplate },
      ];

      Form = CampaignBriefFilter;
      break;

    case BriefType.EXECUTION_BRIEF:
      chipConfig = [
        ...commonChips,
        { name: "briefOwner", class: styles.filterBriefOwner, render: userName },
        { name: "template", class: styles.filterTemplate, render: executionTemplate },
      ];
      Form = ExecutionBriefFilter;
      break;

    case BriefType.WORK_ORDER:
      chipConfig = [
        ...commonChips,
        { name: "assignee", class: styles.filterBriefOwner, render: userName },
        { name: "creativePrime", class: styles.filterTemplate, render: userName },
      ];
      Form = WorkOrderFilter;
      break;
  }

  return {
    chipConfig,
    Form,
  };
}

export default useFilterConfig;
